import { useEffect, useMemo, useState } from 'react';
import { type Path, useFormContext } from 'react-hook-form';
import { Box, Collapse, Tab, Tabs, Typography } from '@mui/material';
import {
  Accordion,
  Chip,
  FadeUp,
  FormSection,
  RhfFractionField,
  RhfSelect,
  RhfTextField,
} from '@core/components';
import { useProgressiveImageSrc } from '@core/hooks/useProgressiveImageSrc';
import { getEntityById } from '@core/utils/collection';
import { toProperCase } from '@core/utils/string';
import type { Vestment } from '../../../api/vestment';
import { RhfCustomerSelect } from '../../components/RhfCustomerSelect';
import { useAppContext } from '../../context/AppContext';
import { VestmentCategory } from '../../enum/vestmentCategory';
import { isV1VestmentType } from '../../enum/vestmentType';
import { useCustomerById } from '../../hooks/useCustomerById';
import {
  ALB_SUBTYPE_OPTIONS,
  getAlbFeatures,
  getAlbPreviewSubtype,
} from '../../utils/alb';
import {
  getChasubleFeatures,
  isChasubleTypeAvailable,
  showsChasubleCollar,
  sortChasubleVestments,
} from '../../utils/chasuble';
import { usesVestmentCollars } from '../../utils/collar';
import { designMatchesVestmentType } from '../../utils/design';
import { getImageFileUrl, getImageThumbnailUrl } from '../../utils/image';
import { formatMeasurement } from '../../utils/measurement';
import {
  type AlbItemFormValues,
  type OrderItemDialogSlotProps,
  type OrderItemFormValues,
  getMeasurementPlaceholder,
  getVestmentsByCategory,
} from '../../utils/orderItem';
import { ORDER_ITEM_DIALOGS } from '../../utils/orderItem/dialogs';
import { usePersistOrderItemDraft } from '../../utils/orderItem/draft';
import {
  CUSTOMER_SIZE_ID,
  SIZE_FIELD_LABELS,
  filterSizesForVestment,
  forCustomerForSizeSelection,
  getPresetSize,
  getSizeFields,
  isCustomSize,
  isCustomerSize,
} from '../../utils/size';
import { sortStoleVestments } from '../../utils/stole';
import { getVestmentImageUrls } from '../../utils/vestment';
import { CollapsibleField } from './CollapsibleField';
import { OrderItemSummaryRow } from './OrderItemDialog';
import { RhfDesignPicker } from './RhfDesignPicker';
import SizePicker from './pickers/SizePicker';

const VESTMENT_OPTION_CHIPS = {
  retired: { label: 'Retired', tone: 'grey' },
  comingSoon: { label: 'Coming soon', tone: 'blue' },
} as const;

export interface OrderItemDialogContentProps {
  variant: 'add' | 'edit';
  category: VestmentCategory;
  orderId?: string | number | null;
  orderCustomerId?: string | number | null;
  expandedSection?: string;
}

const OrderItemDialogContent = ({
  variant,
  category,
  orderId,
  orderCustomerId,
  expandedSection: initialExpandedSection,
}: OrderItemDialogContentProps) => {
  const { sizes, vestments } = useAppContext();
  const methods = useFormContext<OrderItemFormValues>();
  const dialog = ORDER_ITEM_DIALOGS[category];

  usePersistOrderItemDraft(methods, {
    enabled: variant === 'add',
    orderId,
    category,
  });

  const [expandedSection, setExpandedSection] = useState<string | null>(
    initialExpandedSection ?? null
  );
  const [previewTab, setPreviewTab] = useState<'vestment' | 'design'>(
    'vestment'
  );

  useEffect(() => {
    setExpandedSection(initialExpandedSection ?? null);
    setPreviewTab('vestment');
  }, [initialExpandedSection]);

  const isChasuble = category === VestmentCategory.Chasuble;
  const isStole = category === VestmentCategory.Stole;
  const isAlb = category === VestmentCategory.Alb;
  const vestmentId = methods.watch('vestment');
  const design = methods.watch('design');
  const designCode = methods.watch('designCode');
  const collarId = methods.watch('collar');
  const stoleCutId = String(
    methods.watch('stoleCut' as Path<OrderItemFormValues>) ?? ''
  );
  const albSubtype = String(
    methods.watch('subtype' as Path<OrderItemFormValues>) ?? ''
  );
  const albLaceStyle = String(
    methods.watch('laceStyle' as Path<OrderItemFormValues>) ?? ''
  );
  const sizeId = methods.watch('size');
  const forCustomerId = methods.watch('forCustomer');
  const notes = methods.watch('notes') ?? '';
  const categoryVestments = useMemo(() => {
    const items = getVestmentsByCategory(vestments, category);
    if (isChasuble) {
      return sortChasubleVestments(items);
    }
    if (isStole) {
      return sortStoleVestments(items);
    }
    return items;
  }, [vestments, category, isChasuble, isStole]);
  const savedVestmentId =
    variant === 'edit' ? methods.formState.defaultValues?.vestment : undefined;
  const vestmentOptions = categoryVestments.filter(
    (v) => !v.isHidden || String(v.id) === savedVestmentId
  );
  const vestment = getEntityById(categoryVestments, vestmentId);
  const categorySizes = filterSizesForVestment(sizes, category, vestment?.type);
  const { data: forCustomer } = useCustomerById(forCustomerId);
  const albFeatures = getAlbFeatures(vestment?.type);
  const showsAlbSubtype = !!albFeatures?.hasSubtypes;
  const showsLegacyAlbDesign =
    isAlb && !!vestment?.type && isV1VestmentType(vestment.type);
  const hidesDesign = getChasubleFeatures(vestment?.type)?.bodyType ?? false;

  const sizeFields = getSizeFields(category, vestment?.type);
  const sizeFieldColumns = Math.min(Math.max(sizeFields.length, 2), 4);
  const sizeFieldRowHeight = 72;
  const sizeFieldMaxHeight =
    sizeId !== ''
      ? {
          xs: Math.ceil(sizeFields.length / 2) * sizeFieldRowHeight,
          md:
            Math.ceil(sizeFields.length / sizeFieldColumns) *
            sizeFieldRowHeight,
        }
      : 0;
  const selectedSize = getPresetSize(categorySizes, sizeId);
  const useCustomerSize = isCustomerSize(sizeId);
  const measurementContext = {
    customer: forCustomer,
    size: selectedSize,
    vestmentType: vestment?.type,
    vestmentCategory: category,
  };

  const previewsCollar =
    usesVestmentCollars(vestment?.type) && showsChasubleCollar(vestment?.type);
  const collar = previewsCollar
    ? getEntityById(vestment?.collars ?? [], collarId)
    : undefined;
  const stoleCut = isStole
    ? getEntityById(vestment?.stoleCuts ?? [], stoleCutId)
    : undefined;
  const albPreviewSubtype = getAlbPreviewSubtype(
    vestment?.albSubtypes,
    albSubtype,
    albLaceStyle
  );
  const vestmentImages = getVestmentImageUrls(
    vestment,
    collar,
    stoleCut,
    albPreviewSubtype
  );
  const vestmentPreviewUrl = useProgressiveImageSrc(
    vestmentImages.thumbnailUrl,
    previewTab === 'vestment' ? vestmentImages.imageUrl : null
  );
  const designImageUrl = design ? getImageFileUrl(design) : null;
  const designPreviewUrl = useProgressiveImageSrc(
    design ? getImageThumbnailUrl(design) : null,
    previewTab === 'design' ? designImageUrl : null
  );
  const activePreviewUrl =
    previewTab === 'vestment' ? vestmentPreviewUrl : designPreviewUrl;
  const activePreviewKey =
    previewTab === 'vestment' ? vestmentImages.imageUrl : designImageUrl;
  const previewPlaceholder =
    previewTab === 'design'
      ? designCode
        ? 'No preview available'
        : 'Select a design to preview'
      : !vestment
        ? 'Select a vestment to preview'
        : 'No preview available';

  /**
   * Clears the values the new type doesn't use. The design is replaced by the
   * new type's default design code (or blank) when it is empty, the previous
   * type's default, or a design of another type. Coming from a type without
   * size fields, an empty size starts again from the order customer's size.
   */
  const onVestmentChange = (selectedVestmentId: string) => {
    const nextType = getEntityById(categoryVestments, selectedVestmentId)?.type;
    const nextFeatures = getChasubleFeatures(nextType);
    const currentDesign = methods.getValues('design');
    const currentCode = methods.getValues('designCode').trim();
    const replacesDesign =
      !currentCode ||
      currentCode === getChasubleFeatures(vestment?.type)?.defaultDesignCode ||
      (!!nextType &&
        !!currentDesign &&
        !designMatchesVestmentType(currentDesign, nextType));

    const unusedFields: Path<OrderItemFormValues>[] = [
      ...(nextFeatures?.sizeFields.length === 0
        ? (['size', 'forCustomer', 'length', 'sleeve', 'neck'] as const)
        : []),
      ...(nextFeatures && !nextFeatures.centerFinish
        ? (['centerFinish'] as const)
        : []),
    ];
    for (const name of unusedFields) {
      if (methods.getValues(name)) {
        methods.setValue(name, '', { shouldDirty: true });
      }
    }
    if (getAlbFeatures(nextType)?.designTypes.length === 0) {
      if (currentDesign) {
        methods.setValue('design', null, { shouldDirty: true });
      }
      if (currentCode) {
        methods.setValue('designCode', '', { shouldDirty: true });
      }
    } else if (replacesDesign) {
      if (currentDesign) {
        methods.setValue('design', null, { shouldDirty: true });
      }
      methods.setValue('designCode', nextFeatures?.defaultDesignCode ?? '', {
        shouldDirty: true,
      });
    }

    const currentSizeId = methods.getValues('size');
    if (
      category === VestmentCategory.Stole &&
      currentSizeId &&
      !isCustomerSize(currentSizeId) &&
      !isCustomSize(currentSizeId)
    ) {
      const nextSizes = filterSizesForVestment(sizes, category, nextType);
      const stillSelected = nextSizes.some(
        (size) => String(size.id) === currentSizeId
      );
      if (!stillSelected) {
        const currentName = getEntityById(sizes, currentSizeId)?.name;
        const sameName = currentName
          ? nextSizes.find((size) => size.name === currentName)
          : undefined;
        if (sameName) {
          methods.setValue('size', String(sameName.id), { shouldDirty: true });
          methods.setValue('forCustomer', '', { shouldDirty: true });
        } else {
          methods.setValue('size', CUSTOMER_SIZE_ID, { shouldDirty: true });
          methods.setValue(
            'forCustomer',
            forCustomerForSizeSelection(CUSTOMER_SIZE_ID, orderCustomerId),
            { shouldDirty: true }
          );
        }
      }
    }

    const restoresSize =
      sizeFields.length === 0 &&
      getSizeFields(category, nextType).length > 0 &&
      !methods.getValues('size');
    const customerSizeCustomer = forCustomerForSizeSelection(
      CUSTOMER_SIZE_ID,
      orderCustomerId
    );
    if (restoresSize && customerSizeCustomer) {
      methods.setValue('size', CUSTOMER_SIZE_ID, { shouldDirty: true });
      methods.setValue('forCustomer', customerSizeCustomer, {
        shouldDirty: true,
      });
    }
    setPreviewTab('vestment');
  };

  const onSizeChange = (selectedSizeId: string) => {
    methods.setValue(
      'forCustomer',
      forCustomerForSizeSelection(selectedSizeId, orderCustomerId),
      { shouldDirty: true }
    );
  };

  const slot: OrderItemDialogSlotProps<OrderItemFormValues> = {
    methods,
    expandedSection,
    setExpandedSection,
    setPreviewTab,
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        gap: 2,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flex: { md: 2 },
          flexDirection: 'column',
        }}
      >
        <Accordion title={toProperCase(category)} expanded onChange={() => {}}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              gap: 2,
            }}
          >
            <RhfSelect<OrderItemFormValues, Vestment>
              name="vestment"
              label="Type"
              items={vestmentOptions}
              getOptionValue={(v) => String(v.id)}
              getOptionLabel={(v) => v.name}
              getOptionDisabled={
                isChasuble ? (v) => !isChasubleTypeAvailable(v.type) : undefined
              }
              renderOption={({ item, label }) => {
                const chip = item.isHidden
                  ? VESTMENT_OPTION_CHIPS.retired
                  : isChasuble && !isChasubleTypeAvailable(item.type)
                    ? VESTMENT_OPTION_CHIPS.comingSoon
                    : null;
                if (!chip) {
                  return label;
                }
                return (
                  <Box
                    component="span"
                    sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                  >
                    {label}
                    <Chip
                      size="xs"
                      variant="outlined"
                      label={chip.label}
                      sx={{
                        color: `${chip.tone}.800`,
                        borderColor: `${chip.tone}.500`,
                      }}
                    />
                  </Box>
                );
              }}
              onValueChange={onVestmentChange}
              required
            />
            <CollapsibleField
              collapsed={
                isAlb ? !showsAlbSubtype && !showsLegacyAlbDesign : hidesDesign
              }
            >
              {showsAlbSubtype ? (
                <RhfSelect<AlbItemFormValues>
                  name="subtype"
                  label="Subtype"
                  options={ALB_SUBTYPE_OPTIONS}
                />
              ) : (
                <RhfDesignPicker
                  key={vestmentId}
                  vestmentType={vestment?.type}
                  disabled={!vestment}
                  onValueChange={() => setPreviewTab('design')}
                />
              )}
            </CollapsibleField>
          </Box>
        </Accordion>

        <Accordion
          title="Quantity"
          expanded={expandedSection === 'quantity'}
          onChange={(expanded) =>
            setExpandedSection(expanded ? 'quantity' : null)
          }
        >
          <Box sx={{ display: 'flex', gap: 2 }}>
            <RhfTextField<OrderItemFormValues>
              name="quantity"
              label="Quantity"
              required
              textFieldProps={{
                type: 'number',
                InputProps: { inputProps: { min: 1 } },
                sx: { flex: 1, width: { xs: '100%', md: 'auto' } },
              }}
            />
            <Box sx={{ flex: 1, display: { xs: 'none', md: 'block' } }} />
          </Box>
        </Accordion>

        <Accordion
          title="Size"
          expanded={expandedSection === 'size'}
          collapsed={sizeFields.length === 0}
          onChange={(expanded) => setExpandedSection(expanded ? 'size' : null)}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <SizePicker
              sizes={categorySizes}
              value={sizeId}
              onChange={(selectedSizeId) => {
                methods.setValue('size', selectedSizeId, {
                  shouldDirty: true,
                });
                onSizeChange(selectedSizeId);
              }}
            />
            <Box
              sx={{
                mt: useCustomerSize ? 2 : 0,
                maxHeight: useCustomerSize ? 72 : 0,
                opacity: useCustomerSize ? 1 : 0,
                overflow: useCustomerSize ? 'visible' : 'hidden',
                transform: useCustomerSize
                  ? 'translateY(0)'
                  : 'translateY(-8px)',
                transition:
                  'max-height 0.2s ease-in-out, margin-top 0.2s ease-in-out, opacity 0.2s ease-in-out, transform 0.2s ease-in-out',
              }}
            >
              <RhfCustomerSelect<OrderItemFormValues>
                name="forCustomer"
                label="Customer"
                showMeasurements
                sx={{ width: '100%' }}
              />
            </Box>
          </Box>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr 1fr',
                md: `repeat(${sizeFieldColumns}, 1fr)`,
              },
              gap: 2,
              maxHeight: sizeFieldMaxHeight,
              opacity: sizeId !== '' ? 1 : 0,
              transition:
                'max-height 0.3s ease-in-out, opacity 0.3s ease-in-out',
            }}
          >
            {sizeFields.map((field) => (
              <RhfFractionField<OrderItemFormValues>
                key={field}
                name={field}
                label={SIZE_FIELD_LABELS[field]}
                placeholder={getMeasurementPlaceholder(
                  field,
                  measurementContext
                )}
              />
            ))}
          </Box>
        </Accordion>

        {dialog.renderForm(slot)}

        <Accordion
          title="Notes"
          expanded={expandedSection === 'notes'}
          onChange={(expanded) => setExpandedSection(expanded ? 'notes' : null)}
        >
          <RhfTextField<OrderItemFormValues>
            name="notes"
            textFieldProps={{ multiline: true, rows: 3 }}
          />
        </Accordion>
      </Box>

      <FormSection title="Summary" sx={{ flex: { md: 1 } }}>
        <Box
          sx={{
            borderRadius: 2,
            overflow: 'hidden',
            border: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Tabs
            value={previewTab}
            onChange={(_, value: 'vestment' | 'design') => setPreviewTab(value)}
            variant="fullWidth"
            sx={{
              minHeight: 30,
              borderBottom: 1,
              borderColor: 'divider',
              bgcolor: 'action.hover',
              '& .MuiTab-root': {
                minHeight: 30,
                py: 0.5,
                fontSize: (theme) => theme.typography.caption.fontSize,
                fontWeight: 500,
                textTransform: 'none',
                color: 'text.secondary',
              },
              '& .Mui-selected': {
                color: 'text.primary',
              },
            }}
          >
            <Tab label="Vestment" value="vestment" />
            <Tab label="Design" value="design" />
          </Tabs>
          <Box
            sx={{
              height: 244,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {activePreviewUrl ? (
              <FadeUp key={`${previewTab}-${activePreviewKey}`}>
                <Box
                  component="img"
                  src={activePreviewUrl}
                  sx={{
                    objectFit: 'contain',
                    width: '100%',
                    maxHeight: 230,
                  }}
                />
              </FadeUp>
            ) : (
              <Typography variant="caption" color="text.secondary">
                {previewPlaceholder}
              </Typography>
            )}
          </Box>
          <Collapse
            in={getChasubleFeatures(vestment?.type)?.centerFinish ?? false}
            unmountOnExit
          >
            <Box sx={{ p: 1, display: 'flex', justifyContent: 'center' }}>
              <RhfFractionField<OrderItemFormValues>
                name="centerFinish"
                label="Center Finish (in)"
                placeholder={
                  formatMeasurement(design?.centerFinish) ?? undefined
                }
                sx={{
                  maxWidth: 125,
                  '& .MuiInputBase-root, & .MuiInputLabel-root': {
                    fontSize: (theme) => theme.typography.body2.fontSize,
                  },
                  '& .MuiInputBase-input': { py: 0.75, textAlign: 'center' },
                  '& .MuiInputLabel-root': {
                    width: '100%',
                    maxWidth: '100%',
                    textAlign: 'center',
                    transformOrigin: 'top center',
                    transform: 'translate(0, 6px) scale(1)',
                  },
                  '& .MuiInputLabel-shrink': {
                    transform: 'translate(0, -9px) scale(0.75)',
                  },
                  '& legend': { mx: 'auto' },
                }}
              />
            </Box>
          </Collapse>
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 0.5,
            mx: 2,
          }}
        >
          {dialog.renderSummary(slot)}
          {notes.trim() ? (
            <OrderItemSummaryRow label="Notes" fadeKey={notes}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ whiteSpace: 'pre-line', wordBreak: 'break-word' }}
              >
                {notes.trim()}
              </Typography>
            </OrderItemSummaryRow>
          ) : null}
        </Box>
      </FormSection>
    </Box>
  );
};

export default OrderItemDialogContent;
